import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScrolled } from '../../hooks/useScrolled';
import LanguageSwitcher from './LanguageSwitcher';

export default function Navbar() {
  const scrolled = useScrolled(60);
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const isHome = location.pathname === '/';
  const { t } = useTranslation();

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'instant' });

  const navLinks = [
    { labelKey: 'nav.home', path: '/' },
    { labelKey: 'nav.about', path: '/about' },
    { labelKey: 'nav.services', path: '/services' },
    { labelKey: 'nav.partners', path: '/partners' },
    { labelKey: 'nav.news', path: '/news' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !isHome
          ? 'bg-navy shadow-sm border-b border-navy-light/30'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" onClick={scrollToTop} className="flex items-center gap-3 cursor-pointer">
          <div className="w-10 h-10 flex items-center justify-center">
            <img
              src="https://public.readdy.ai/ai/img_res/6d7c6fc4-cc3c-4fea-be07-307c9a982110.png"
              alt="MONARU Logo"
              className="w-10 h-10 object-contain"
            />
          </div>
          <div className="flex flex-col">
            <span className="font-serif text-base font-bold text-white leading-tight tracking-wide">
              MONARU
            </span>
            <span className="text-[10px] font-sans text-silver tracking-[0.2em] uppercase leading-none">
              Korea · Vietnam
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => {
            const active = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                onClick={scrollToTop}
                className={`px-4 py-2 text-sm font-sans font-medium tracking-wide transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  active
                    ? 'text-white border-b border-silver'
                    : 'text-silver/80 hover:text-white'
                }`}
              >
                {t(link.labelKey)}
              </Link>
            );
          })}
        </nav>

        {/* Right Controls */}
        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          <Link
            to="/contact"
            onClick={scrollToTop}
            className="px-5 py-2.5 text-sm font-sans font-medium border border-silver text-silver hover:bg-silver hover:text-navy transition-all duration-200 rounded cursor-pointer whitespace-nowrap"
          >
            {t('nav.getInTouch')}
          </Link>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-2xl`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-navy border-t border-navy-light/30 px-6 py-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => { setMenuOpen(false); scrollToTop(); }}
              className="block py-3 text-sm font-sans text-silver hover:text-white border-b border-navy-light/30 transition-colors duration-200 cursor-pointer"
            >
              {t(link.labelKey)}
            </Link>
          ))}
          <div className="flex items-center justify-between mt-4 pt-2">
            <LanguageSwitcher dropUp align="left" />
            <Link
              to="/contact"
              onClick={() => { setMenuOpen(false); scrollToTop(); }}
              className="py-2.5 px-5 text-sm font-sans font-medium border border-silver text-silver hover:bg-silver hover:text-navy transition-all duration-200 rounded cursor-pointer whitespace-nowrap"
            >
              {t('nav.getInTouch')}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
